<template>
  <Transition name="mq-modal-fade">
    <div v-if="open" class="mq-mask" @click.self="handleClose" @keydown.esc="handleClose">
      <div class="mq-dialog-stack" role="dialog" aria-modal="true" aria-labelledby="abot-mq-title" tabindex="-1"
        @click.self="handleClose" @keydown.esc="handleClose">
        <div class="mq-modal">
          <h2 id="abot-mq-title" class="mq-title">
            {{ $t('session.chat.matchQuestion.title') }}
          </h2>
          <div class="mq-list" tabindex="0">
            <div v-for="(item, index) in items" :key="index" class="mq-item">
              <div class="mq-item-header">
                <component :is="getAvatar(item.avatar)" class="mq-item-avatar" />
                <span class="mq-item-meta">
                  <span class="mq-item-name">{{ item.createBy || '-' }}</span>
                  <span class="mq-item-time">{{ formatMatchQuestionDate(item.createTime) }}</span>
                </span>
              </div>
              <div class="mq-item-answer">{{ item.answer }}</div>
              <button type="button" class="mq-item-solve"
                :class="{ 'is-solved': isSolved(item.queId), 'is-locked': hasAnySolved && !isSolved(item.queId) }"
                :disabled="isSolved(item.queId) || isSubmitting(item.queId) || (hasAnySolved && !isSolved(item.queId))"
                @click="handleSolve(item)">
                <svg v-if="isSolved(item.queId)" width="14" height="14" viewBox="0 0 14 14" fill="none"
                  xmlns="http://www.w3.org/2000/svg">
                  <path
                    d="M12.7019 5.50295C12.2936 4.97251 11.676 4.66839 11.0071 4.66839H8.42022L9.04089 2.98295C9.401 2.00606 8.97011 0.917949 8.03833 0.45206C7.79956 0.33306 7.51022 0.390616 7.336 0.592838L4.01956 4.43273C3.68433 4.82162 3.5 5.31784 3.5 5.83039V10.3073C3.5 11.4872 4.45978 12.4462 5.63889 12.4462H10.08C11.0491 12.4462 11.8992 11.7921 12.1481 10.8548L13.0744 7.35484C13.2456 6.70773 13.1094 6.03339 12.7019 5.50295Z"
                    fill="#2E9B4A" />
                  <path
                    d="M3.30561 12.4418H2.13894C1.38839 12.4418 0.777832 11.8313 0.777832 11.0807V6.02517C0.777832 5.27462 1.38839 4.66406 2.13894 4.66406H3.30561C4.05617 4.66406 4.66672 5.27462 4.66672 6.02517V11.0807C4.66672 11.8313 4.05617 12.4418 3.30561 12.4418ZM2.13894 5.83073C2.03161 5.83073 1.9445 5.91784 1.9445 6.02517V11.0807C1.9445 11.1881 2.03161 11.2752 2.13894 11.2752H3.30561C3.41294 11.2752 3.50005 11.1881 3.50005 11.0807V6.02517C3.50005 5.91784 3.41294 5.83073 3.30561 5.83073H2.13894Z"
                    fill="#2E9B4A" />
                </svg>
                {{
                  isSolved(item.queId) ? $t('session.chat.solved') : $t('session.chat.unsolved') }}</button>
            </div>
          </div>
        </div>
        <button type="button" class="mq-close-button" :aria-label="$t('common.close')" @click="handleClose">
          <svg width="12" height="12" viewBox="0 0 12 12" fill="none" aria-hidden="true">
            <path d="M2 2L10 10" stroke="currentColor" stroke-width="1.4" stroke-linecap="round"
              stroke-linejoin="round" />
            <path d="M2 10L10 2" stroke="currentColor" stroke-width="1.4" stroke-linecap="round"
              stroke-linejoin="round" />
          </svg>
        </button>
      </div>
    </div>
  </Transition>
</template>

<script lang="ts">
/**
 * Purpose: Render the "该问题可能有多个回答" modal invoked from MatchQuestionsCard.
 * ChatDialog owns the open state and the items payload; this component owns the
 * submit state, the queClicks API call, and the modal DOM/styles.
 */
import { computed, defineComponent, onUnmounted, ref, watch, type PropType } from 'vue'
import { useChatStore } from '../../../store/chat'
import { queClicks } from '../../../api/simple-api'
import { getChannelId } from '../../../utils/channelStorage'
import { resolveAvatar } from '../../avatar/resolveAvatar'
import {
  formatMatchQuestionDate,
  type MatchQuestionItem,
} from './matchQuestionsTypes'

export default defineComponent({
  name: 'MatchQuestionsModal',
  props: {
    open: { type: Boolean, required: true },
    items: { type: Array as PropType<MatchQuestionItem[]>, required: true },
    messageId: { type: String, required: true },
  },
  emits: {
    close: () => true,
  },
  setup(props, { emit }) {
    const cStore = useChatStore()
    const submitting = ref<Record<string, true>>({})

    const handleClose = () => emit('close')

    const handleKeydown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') handleClose()
    }

    watch(
      () => props.open,
      (isOpen) => {
        if (isOpen) {
          window.addEventListener('keydown', handleKeydown)
          return
        }
        window.removeEventListener('keydown', handleKeydown)
      },
    )

    onUnmounted(() => {
      window.removeEventListener('keydown', handleKeydown)
    })

    const isSolved = (queId: string) =>
      cStore.isMatchQuestionSolved(props.messageId, queId)

    const hasAnySolved = computed(() =>
      props.items.some((item) => isSolved(item.queId)),
    )

    const isSubmitting = (queId: string) => Boolean(submitting.value[queId])

    // "解决问题"点击：调 GET /merchant/channel/queClicks 打点。
    // 成功后把 (messageId, queId) 标记为已解决——按钮变灰、文案切成"已解决问题"。
    // 失败保留原状让用户可重试；请求飞行中禁用避免重复点击。
    const handleSolve = async (item: MatchQuestionItem) => {
      const messageId = props.messageId
      if (!messageId || !item.queId) return
      if (isSolved(item.queId)) return
      if (hasAnySolved.value) return
      if (submitting.value[item.queId]) return
      submitting.value = { ...submitting.value, [item.queId]: true }
      try {
        await queClicks({ messageId, queId: item.queId }, getChannelId())
        cStore.markMatchQuestionSolved(messageId, item.queId)
      } catch (e) {
        console.warn('[MQ] queClicks 失败:', e)
      } finally {
        const next = { ...submitting.value }
        delete next[item.queId]
        submitting.value = next
      }
    }

    const getAvatar = (id: string) => resolveAvatar(id)

    return {
      handleClose,
      handleSolve,
      isSolved,
      isSubmitting,
      hasAnySolved,
      formatMatchQuestionDate,
      getAvatar
    }
  },
})
</script>

<style lang="less" scoped>
.mq-mask {
  position: absolute;
  inset: 0;
  z-index: 10;
  display: flex;
  box-sizing: border-box;
  width: 100%;
  height: 100%;
  padding-top: max(24px, env(safe-area-inset-top, 0px));
  padding-bottom: max(24px, env(safe-area-inset-bottom, 0px));
  align-items: center;
  justify-content: center;
  background: rgba(32, 34, 33, 0.38);
  backdrop-filter: blur(4px);
  -webkit-backdrop-filter: blur(4px);
}

.mq-dialog-stack {
  display: flex;
  width: calc(100% - 60px);
  height: 100%;
  min-height: 0;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 12px;
}

.mq-modal {
  box-sizing: border-box;
  display: flex;
  width: 100%;
  max-height: calc(100% - 36px);
  min-height: 0;
  padding: 24px 36px;
  flex-direction: column;
  align-items: stretch;
  justify-content: flex-start;
  gap: 12px;
  border-radius: 12px;
  background: #fff;
}

.compact-dialog .mq-mask {
  padding-top: max(16px, env(safe-area-inset-top, 0px));
  padding-bottom: max(16px, env(safe-area-inset-bottom, 0px));
}

.compact-dialog .mq-modal {
  padding: 16px 24px;
}

.mq-title {
  align-self: stretch;
  flex-shrink: 0;
  margin: 0;
  display: flex;
  align-items: center;
  color: #202221;
  font-family: inherit;
  font-size: 14px;
  font-style: normal;
  font-weight: 500;
  line-height: 22px;
  text-align: left;
}

.mq-title-icon {
  flex: 0 0 16px;
}

.mq-list {
  width: 100%;
  min-height: 0;
  min-width: 0;
  overflow-y: auto;
  overscroll-behavior: contain;
  scrollbar-color: var(--text-4, #b8bbb8) transparent;
  scrollbar-width: thin;
  -webkit-overflow-scrolling: touch;
  display: flex;
  flex-direction: column;
  gap: 8px;

  &:focus-visible {
    outline: 2px solid var(--abot-brand-color);
    outline-offset: 2px;
  }

  &::-webkit-scrollbar {
    width: 4px;
  }

  &::-webkit-scrollbar-track {
    background: transparent;
  }

  &::-webkit-scrollbar-thumb {
    border-radius: 100px;
    background: var(--text-4, #b8bbb8);
  }

  &::-webkit-scrollbar-thumb:hover {
    background: var(--text-3, #979c98);
  }
}

.mq-item {
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 8px 12px;
  background-color: #f9faf9;
  border-radius: 8px;
}

.mq-item-header {
  display: flex;
  align-items: center;
  gap: 8px;
}

.mq-item-index {
  flex-shrink: 0;
  width: 20px;
  height: 20px;
  border-radius: 50%;
  background: #e0e3e0;
  color: var(--text-2, #616961);
  font-size: 12px;
  font-weight: 500;
  line-height: 20px;
  text-align: center;
}

.mq-item-avatar {
  width: 32px;
  height: 32px;
  border-radius: 50%;
  overflow: hidden;
}

.mq-item-meta {
  display: flex;
  flex-direction: column;
  gap: -2px;
  min-width: 0;
  overflow: hidden;
}

.mq-item-name {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  font-size: 14px;
  line-height: 22px;
  font-weight: 400;
  color: #202221;
}

.mq-item-time {
  color: #9c9f9d;
  font-weight: 400;
  white-space: nowrap;
  font-size: 12px;
  line-height: 20px;
}

.mq-item-answer {
  color: #5f6260;
  font-size: 12px;
  font-weight: 400;
  line-height: 18px;
  overflow-wrap: anywhere;
  white-space: pre-line;
}

.mq-item-solve {
  align-self: flex-start;
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 1px 8px;
  border: 1px solid #edefed;
  border-radius: 4px;
  background: #fff;
  color: #9c9f9d;
  font-family: inherit;
  font-size: 12px;
  font-weight: 400;
  line-height: 20px;
  cursor: pointer;

  &:hover {
    border-color: #e8f5e9;
    color: #258b3e;
    background-color: #e8f5e9;
  }

  &:focus-visible {
    outline: 2px solid #e8f5e9;
    outline-offset: 2px;
  }

  &:disabled {
    cursor: not-allowed;
  }

  &.is-solved,
  &.is-solved:hover {
    background: #e8f5e9;
    border-color: #e8f5e9;
    color: #258b3e;
    cursor: not-allowed;
  }
}

.mq-close-button {
  box-sizing: border-box;
  display: flex;
  width: 24px;
  height: 24px;
  padding: 0;
  flex: 0 0 24px;
  align-items: center;
  justify-content: center;
  border: 0;
  border-radius: 100px;
  background: rgba(0, 0, 0, 0.6);
  color: #9c9f9d;
  cursor: pointer;

  &:focus-visible {
    outline: 2px solid #fff;
    outline-offset: 2px;
  }
}

.mq-modal-fade-enter-active {
  transition: opacity 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.mq-modal-fade-leave-active {
  transition: opacity 0.2s cubic-bezier(0.4, 0, 1, 1);
}

.mq-modal-fade-enter-from,
.mq-modal-fade-leave-to {
  opacity: 0;
}

.mq-modal-fade-enter-active .mq-dialog-stack,
.mq-modal-fade-leave-active .mq-dialog-stack {
  transition: transform 0.2s ease;
}

.mq-modal-fade-enter-from .mq-dialog-stack,
.mq-modal-fade-leave-to .mq-dialog-stack {
  transform: scale(0.96);
}
</style>
